import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetScratchEventQuery,
  useLazyGetScratchEventQuery,
} from '@/api/queries/scratch';
import { useScratchEventListener } from '@/common/hooks/scratch-event-listener';
import { Playdeck } from '@/common/lib/playdeck';
import { scratchSelectionStorage } from '@/common/lib/scratchSelectionStorage';
import { type ScratchOffer } from '@/common/types/scratch';
import { ResourceTitle } from '@/components/ResourceTitle/ResourceTitle';
import { ScratchBackground } from '@/components/Scratch/ScratchBackground/ScratchBackground';
import { ScratchEventTimer } from '@/components/Scratch/ScratchEventTimer/ScratchEventTimer';
import { ScratchInfoModal } from '@/components/Scratch/ScratchInfoModal/ScratchInfoModal';
import { ScratchOfferCard } from '@/components/Scratch/ScratchOfferCard/ScratchOfferCard';
import { ScratchPrizeModal } from '@/components/Scratch/ScratchPrizeModal/ScratchPrizeModal';
import { ScratchRewardPicker } from '@/components/Scratch/ScratchRewardPicker/ScratchRewardPicker';
import { StringSmall } from '@/components/Typography/Typography';
import {
  initFromApi,
  selectOfferReward,
} from '@/redux/reducers/scratchReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

const Scratch: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const offers = useAppSelector((s) => s.scratch.offers);
  const event = useAppSelector((s) => s.scratch.event);

  // Pull the canonical event payload from server. Hydrates Redux on every
  // arrival via initFromApi.
  const { data: eventData } = useGetScratchEventQuery();
  const [refetchEvent] = useLazyGetScratchEventQuery();

  useEffect(() => {
    if (!eventData) return;

    // Wipe local picks when the event rolls over to a new id.
    scratchSelectionStorage.pruneIfStale(eventData.id);

    dispatch(initFromApi(eventData));

    // After hydrate, replay stored picks for this same event so the
    // picker cell still shows the player's previous choice across
    // page reloads. On a brand-new event id this is a no-op.
    const stored = scratchSelectionStorage.getForEvent(eventData.id);

    if (stored) {
      Object.entries(stored).forEach(([offerId, rewardKey]) => {
        if (rewardKey) {
          dispatch(
            selectOfferReward({
              offerId: offerId as ScratchOffer['id'],
              rewardKey,
            }),
          );
        }
      });
    }
  }, [dispatch, eventData]);

  // WebSocket — picks up bought / auto-claimed tickets and re-syncs.
  useScratchEventListener();

  // Page-open analytics — fires once per mount.
  useEffect(() => {
    Playdeck.sendAnalitics({
      type: 'custom_event',
      name: 'scratch_page_opened',
      user_properties: {},
      event_properties: {},
    });
  }, []);

  // Auto-refetch when the active event window flips (`endAt` for `active`,
  // `startAt` for `pending`). Server lazily processes the transition on
  // any `getScratchEvent` so we just need to ask at the right moment.
  useEffect(() => {
    if (event.status === 'inactive' || !event.now) return;
    const target = event.status === 'active' ? event.endAt : event.startAt;

    if (!target) return;
    const clientDelta = Date.now() - event.now;
    const fireAt = new Date(target).getTime() + clientDelta;
    const ms = Math.max(500, fireAt - Date.now() + 250);
    const id = window.setTimeout(() => {
      void refetchEvent();
    }, ms);

    return (): void => window.clearTimeout(id);
  }, [event.endAt, event.now, event.startAt, event.status, refetchEvent]);

  // Single picker instance lives at page level — keeps `position: fixed`
  // anchored to viewport even while the offer card animates.
  const [pickerOfferId, setPickerOfferId] = useState<ScratchOffer['id'] | null>(
    null,
  );
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const pickerOffer = pickerOfferId
    ? (offers.find((o) => o.id === pickerOfferId) ?? null)
    : null;

  return (
    <main className={css.page}>
      <ScratchBackground />

      <div className={css.scrollArea}>
        <ResourceTitle className={css.title}>{t('Scratch')}</ResourceTitle>

        <div className={css.subtitleRow}>
          <StringSmall weight="medium" className={css.subtitle}>
            {t('Scratch the foil to reveal the prize!')}
          </StringSmall>
          <button
            type="button"
            className={css.infoBtn}
            onClick={() => setIsInfoOpen(true)}
            aria-label={t('How it works')}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
              <circle
                cx="8"
                cy="8"
                r="7"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle cx="8" cy="4.5" r="1" fill="currentColor" />
              <path
                d="M8 7v5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className={css.timerRow}>
          <ScratchEventTimer />
        </div>

        <section className={css.offers}>
          {offers.map((offer) => (
            <ScratchOfferCard
              key={offer.id}
              offer={offer}
              onOpenPicker={() => setPickerOfferId(offer.id)}
            />
          ))}
        </section>
      </div>

      <ScratchPrizeModal />

      <ScratchInfoModal
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
      />

      <ScratchRewardPicker
        open={!!pickerOffer}
        selectedKey={pickerOffer?.selectedRewardKey ?? null}
        rewardPool={pickerOffer?.rewardPool ?? []}
        onClose={() => setPickerOfferId(null)}
        onPick={(key) => {
          if (pickerOfferId) {
            dispatch(
              selectOfferReward({ offerId: pickerOfferId, rewardKey: key }),
            );

            // Mirror the pick into localStorage so it survives reload —
            // scoped to event.id so a new event resets selections.
            if (event.id) {
              scratchSelectionStorage.setSelection(
                event.id,
                pickerOfferId,
                key,
              );
            }

            // Send analytics so we can track which rewards / which offer
            // tier players go for. Includes the offer's reward-pool index
            // (1-based) and rarity to read distributions later.
            const offer = offers.find((o) => o.id === pickerOfferId);
            const prize = offer?.rewardPool.find(
              (p) => `${p.rewardType}-${p.value}` === key,
            );
            const cellIdx =
              offer?.rewardPool.findIndex(
                (p) => `${p.rewardType}-${p.value}` === key,
              ) ?? -1;
            Playdeck.sendAnalitics({
              type: 'custom_event',
              name: 'scratch_reward_selected',
              user_properties: {},
              event_properties: {
                offer_id: pickerOfferId,
                offer_paid: offer?.paid ?? false,
                reward_key: key,
                reward_type: prize?.rewardType ?? null,
                reward_value: prize?.value ?? null,
                rarity: prize?.rarity ?? null,
                cell_position: cellIdx >= 0 ? cellIdx + 1 : null,
              },
            });
          }
          setPickerOfferId(null);
        }}
      />
    </main>
  );
};

export { Scratch };
